import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Broadcast, Plus, FileArrowUp, Warning, Trash, PlayCircle, Pause, CheckCircle, Users, X } from "@phosphor-icons/react";
import { useGeneralSettings } from "@/hooks/useGeneralSettings";

const CHIP_AGE_OPTIONS = [
  { v: "antigo", label: "Chip Antigo", detail: "60–120s entre envios (chip 90+ dias)" },
  { v: "moderado", label: "Chip Moderado", detail: "120–240s entre envios (chip 30-90 dias)" },
  { v: "novo", label: "Chip Novo", detail: "240–480s entre envios (mais seguro, chip < 30 dias)" },
];
const MAX_VARIATIONS = 3;

const STATUS_LABEL = {
  running: "Em execução",
  scheduled: "Agendado",
  paused: "Pausado",
  done: "Concluído",
};

export default function CompanyMassSending() {
  const [stats, setStats] = useState({ pending: 0, sent_month: 0, failed: 0, sent_today: 0, daily_limit: 50 });
  const [campaigns, setCampaigns] = useState([]);

  // Import dialog
  const [openImport, setOpenImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState([]); // list of {phone, name, vars}
  const fileRef = useRef(null);

  // New campaign dialog
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [savingCamp, setSavingCamp] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualPhone, setManualPhone] = useState("");
  const [manualName, setManualName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { maskPhone, displayContactName } = useGeneralSettings();
  const [scheduleOn, setScheduleOn] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/mass/stats"),
        api.get("/mass/campaigns"),
      ]);
      setStats(s.data);
      setCampaigns(c.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    load();
    const it = setInterval(load, 8000);
    return () => clearInterval(it);
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/mass/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.total} contatos importados`);
      setImported(data.contacts);
      setOpenImport(false);
      // If a "new campaign" dialog is open, merge the contacts.
      setForm((f) => ({ ...f, contacts: mergeContacts(f.contacts, data.contacts) }));
      // Auto-open the New Campaign dialog if it wasn't already
      setOpenNew(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const startNewCampaign = () => {
    setForm(emptyForm());
    setScheduleOn(false);
    setOpenNew(true);
  };

  const addManualContact = () => {
    const clean = manualPhone.replace(/\D+/g, "");
    if (clean.length < 10) return toast.error("Telefone inválido");
    setForm((f) => ({
      ...f,
      contacts: mergeContacts(f.contacts, [{ phone: clean, name: manualName.trim(), vars: {} }]),
    }));
    setManualPhone("");
    setManualName("");
  };

  const removeContact = (phone) => {
    setForm((f) => ({ ...f, contacts: f.contacts.filter((c) => c.phone !== phone) }));
  };

  const addVariation = () => {
    if (form.variations.length >= MAX_VARIATIONS) return toast.error(`Máximo ${MAX_VARIATIONS} variações`);
    setForm((f) => ({ ...f, variations: [...f.variations, ""] }));
  };
  const updateVariation = (i, v) => {
    setForm((f) => ({ ...f, variations: f.variations.map((x, idx) => (idx === i ? v : x)) }));
  };
  const removeVariation = (i) => {
    setForm((f) => ({ ...f, variations: f.variations.filter((_, idx) => idx !== i) }));
  };

  const setAudioFile = async (file) => {
    if (!file) {
      setForm((f) => ({ ...f, attachment_data: "", attachment_filename: "", attachment_mimetype: "" }));
      return;
    }
    const ok = /(mp3|ogg|wav|m4a|mpeg|opus)/i.test(file.type + " " + file.name);
    if (!ok) return toast.error("Formato de áudio inválido");
    if (file.size > 64 * 1024 * 1024) return toast.error("Áudio maior que 64MB");
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.split(",")[1] || "";
      setForm((f) => ({
        ...f,
        attachment_data: base64,
        attachment_filename: file.name,
        attachment_mimetype: file.type || "audio/ogg",
        attachment_type: "audio",
      }));
      toast.success("Áudio carregado");
    };
    reader.readAsDataURL(file);
  };

  const setMediaFile = async (file) => {
    if (!file) {
      setForm((f) => ({ ...f, attachment_data: "", attachment_filename: "", attachment_mimetype: "", attachment_type: "" }));
      return;
    }
    if (file.size > 64 * 1024 * 1024) return toast.error("Arquivo maior que 64MB");
    let att_type = "document";
    if (file.type.startsWith("image/")) att_type = "image";
    else if (file.type.startsWith("video/")) att_type = "video";
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.split(",")[1] || "";
      setForm((f) => ({
        ...f,
        attachment_data: base64,
        attachment_filename: file.name,
        attachment_mimetype: file.type || "application/octet-stream",
        attachment_type: att_type,
      }));
      toast.success("Anexo carregado");
    };
    reader.readAsDataURL(file);
  };

  const saveCampaign = async () => {
    if (!form.name.trim()) return toast.error("Nome do envio obrigatório");
    if (!form.message.trim()) return toast.error("Mensagem obrigatória");
    if (form.contacts.length === 0) return toast.error("Adicione ao menos 1 contato");
    setSavingCamp(true);
    try {
      const payload = {
        name: form.name.trim(),
        message: form.message,
        variations: form.variations.filter((v) => v.trim()),
        contacts: form.contacts,
        chip_age: form.chip_age,
        send_type: form.send_type,
        attachment_data: form.attachment_data || null,
        attachment_type: form.attachment_type || null,
        attachment_filename: form.attachment_filename || null,
        attachment_mimetype: form.attachment_mimetype || null,
        scheduled_at: scheduleOn && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      };
      await api.post("/mass/campaigns", payload);
      toast.success("Envio criado — disparo em andamento");
      setOpenNew(false);
      setForm(emptyForm());
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingCamp(false); }
  };

  const doAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Excluir esta campanha e todos os pendentes?")) return;
        await api.delete(`/mass/campaigns/${id}`);
      } else {
        await api.post(`/mass/campaigns/${id}/${action}`);
      }
      toast.success("OK");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filteredContacts = form.contacts.filter((c) =>
    !searchTerm ||
    c.phone.includes(searchTerm) ||
    (c.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Marketing</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1 flex items-center gap-3">
            <Broadcast size={36} weight="duotone" className="text-[#0F4C3A]" />
            Envio em massa
          </h1>
          <p className="text-sm text-[#5C5C59] mt-2">Envie mensagens para vários contatos de forma individual.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setOpenImport(true)} data-testid="mass-import-btn" variant="outline" className="rounded-none shadow-brutal">
            <FileArrowUp size={16} weight="bold" className="mr-1.5" /> Importar planilha
          </Button>
          <Button onClick={startNewCampaign} data-testid="mass-new-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
            <Plus size={16} weight="bold" className="mr-1.5" /> Novo envio
          </Button>
        </div>
      </div>

      {/* Anti-ban rules */}
      <div className="bg-white border-l-[4px] border-[#DC2626] border border-[#E5E5E0] shadow-brutal p-5 mb-6">
        <div className="flex items-center gap-2 text-[#DC2626] font-bold mb-2">
          <Warning size={18} weight="fill" /> Risco de bloqueio pela Meta
        </div>
        <p className="text-xs text-[#5C5C59] mb-3">
          Envios em massa são monitorados. Siga as regras abaixo pra proteger seu número.
        </p>
        <ul className="text-xs space-y-1.5 text-[#1A1A1A] list-disc pl-5">
          <li><b>Use chip aquecido:</b> número com mais de 30 dias de uso normal (conversas reais antes de qualquer envio em massa). Chip novo = ban quase certo.</li>
          <li><b>Crie variações da mensagem:</b> 3 a 5 textos diferentes — o sistema rotaciona aleatoriamente. Mensagem idêntica pra 100 contatos é o sinal #1 de spam pra Meta.</li>
          <li><b>Velocidade:</b> ajuste a idade do chip abaixo. Não tente burlar o delay.</li>
          <li><b>Limite diário:</b> {stats.daily_limit} envios/dia. Acima disso é bloqueado.</li>
          <li><b>Listas limpas:</b> só envie pra quem JÁ falou com você. Spam reports somam.</li>
          <li><b>Personalize:</b> use {'{nome}'} ou {'{primeiro_nome}'} em cada mensagem.</li>
        </ul>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Pendentes" value={stats.pending} />
        <StatCard label="Msgs · Mês" value={stats.sent_month} highlight />
        <StatCard label="Falhou" value={stats.failed} danger />
        <StatCard label="Últimas 24h" value={`${stats.sent_today}/${stats.daily_limit}`} />
      </div>

      {/* Campaigns list */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="mass-campaign-list">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-3 px-4 py-2 bg-[#F7F7F5] border-b border-[#E5E5E0] text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">
          <div>Nome</div>
          <div>Chip</div>
          <div>Progresso</div>
          <div>Status</div>
          <div>Criado</div>
          <div>Ações</div>
        </div>
        {campaigns.length === 0 && (
          <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhum envio em massa criado ainda.</div>
        )}
        {campaigns.map((c) => (
          <div key={c.id} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-3 px-4 py-3 border-b border-[#E5E5E0] last:border-b-0" data-testid={`mass-row-${c.id}`}>
            <div>
              <div className="font-semibold text-sm truncate">{c.name}</div>
              <div className="text-[10px] text-[#5C5C59]">{c.total} contato(s)</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#0F4C3A]">
              {c.chip_age}
            </div>
            <div className="text-xs font-mono">
              <span className="text-[#0F4C3A] font-bold">{c.sent}</span>/<span>{c.total}</span>
              {c.failed > 0 && <span className="text-[#DC2626]"> · {c.failed} falha(s)</span>}
            </div>
            <div className="text-[10px] uppercase tracking-widest font-semibold">
              {STATUS_LABEL[c.status] || c.status}
            </div>
            <div className="text-[10px] text-[#5C5C59]">
              {new Date(c.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
            </div>
            <div className="flex items-center gap-1">
              {c.status === "running" && (
                <button onClick={() => doAction(c.id, "pause")} data-testid={`mass-pause-${c.id}`} className="p-1 text-[#0F4C3A] hover:bg-[#EDF3F0]" title="Pausar">
                  <Pause size={14} weight="bold" />
                </button>
              )}
              {c.status === "paused" && (
                <button onClick={() => doAction(c.id, "resume")} data-testid={`mass-resume-${c.id}`} className="p-1 text-[#0F4C3A] hover:bg-[#EDF3F0]" title="Retomar">
                  <PlayCircle size={14} weight="bold" />
                </button>
              )}
              <button onClick={() => doAction(c.id, "delete")} data-testid={`mass-delete-${c.id}`} className="p-1 text-[#DC2626] hover:bg-[#F7F7F5]" title="Excluir">
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Import Dialog */}
      <Dialog open={openImport} onOpenChange={setOpenImport}>
        <DialogContent className="bg-white rounded-none max-w-lg">
          <DialogHeader><DialogTitle>Importar contatos</DialogTitle></DialogHeader>
          <p className="text-xs text-[#5C5C59]">.xlsx, .xls, .csv ou .vcf — até 10.000 contatos</p>
          <div
            onClick={() => fileRef.current?.click()}
            data-testid="mass-import-drop"
            className="mt-3 border-2 border-dashed border-[#0F4C3A]/40 p-8 text-center cursor-pointer hover:bg-[#EDF3F0]/40 transition"
          >
            <FileArrowUp size={40} weight="duotone" className="mx-auto text-[#0F4C3A]" />
            <div className="mt-2 font-semibold text-sm">Arraste o arquivo ou clique para selecionar</div>
            <div className="mt-1 text-[10px] text-[#5C5C59]">.xlsx, .xls, .csv ou .vcf (máximo 10.000 contatos)</div>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.vcf" data-testid="mass-import-file"
                 className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <div className="mt-3 p-3 bg-[#F7F7F5] text-[10px] text-[#1A1A1A] space-y-1">
            <div><b>Formatos aceitos:</b></div>
            <div>• Excel (.xlsx, .xls) ou CSV exportado do Google Contatos, Excel, etc.</div>
            <div>• vCard (.vcf) — formato exportado de iPhone, Outlook, contatos do celular.</div>
            <div className="pt-1"><b>Para planilha (Excel/CSV):</b></div>
            <div>• A primeira linha deve ser o cabeçalho das colunas.</div>
            <div>• Coluna de telefone: <code className="bg-white px-1">telefone</code>, <code className="bg-white px-1">phone</code>, <code className="bg-white px-1">celular</code> ou <code className="bg-white px-1">whatsapp</code>.</div>
            <div>• Telefone com ou sem DDI 55 (ex: <code className="bg-white px-1">5511999998888</code> ou <code className="bg-white px-1">11999998888</code>). Espaços/traços são removidos.</div>
            <div>• Opcionais: <code className="bg-white px-1">nome</code>, <code className="bg-white px-1">email</code>.</div>
            <div>• Demais colunas viram variáveis pro template (ex: <code className="bg-white px-1">cidade</code>, <code className="bg-white px-1">produto</code>).</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenImport(false)} className="rounded-none" disabled={importing}>
              {importing ? "Importando…" : "Cancelar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Campaign Dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-white rounded-none max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo envio em massa</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Nome do envio *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mass-name-input" placeholder="Ex: Promoção de Março" className="rounded-none mt-1" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Contatos *</Label>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => setOpenImport(true)} data-testid="mass-new-import-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                    <FileArrowUp size={14} weight="bold" className="mr-1" /> Importar planilha
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowManualAdd(!showManualAdd)} data-testid="mass-manual-toggle-btn" className="rounded-none">
                    <Plus size={14} weight="bold" className="mr-1" /> Adicionar manual
                  </Button>
                </div>
              </div>
              {showManualAdd && (
                <div className="p-3 border border-[#E5E5E0] mb-2 grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Input value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} placeholder="5511999998888" data-testid="mass-manual-phone" className="rounded-none" />
                  <Input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Nome (opcional)" data-testid="mass-manual-name" className="rounded-none" />
                  <Button onClick={addManualContact} data-testid="mass-manual-add-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Adicionar</Button>
                </div>
              )}
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar contato por nome ou telefone…" data-testid="mass-search-input" className="rounded-none" />
              <div className="text-[11px] text-[#5C5C59] mt-1"><Users size={11} weight="bold" className="inline mr-1" />{form.contacts.length} contato(s) selecionado(s)</div>
              {filteredContacts.length > 0 && (
                <div className="mt-2 border border-[#E5E5E0] max-h-40 overflow-y-auto" data-testid="mass-contacts-list">
                  {filteredContacts.slice(0, 200).map((c) => (
                    <div key={c.phone} className="flex items-center justify-between px-2 py-1 text-xs border-b border-[#E5E5E0] last:border-b-0">
                      <span>{displayContactName(c.name, c.phone) || "—"} <span className="text-[#5C5C59]">· {maskPhone(c.phone)}</span></span>
                      <button onClick={() => removeContact(c.phone)} data-testid={`mass-remove-${c.phone}`} className="text-[#DC2626] hover:opacity-70"><X size={12} weight="bold" /></button>
                    </div>
                  ))}
                  {filteredContacts.length > 200 && (
                    <div className="p-2 text-[10px] text-center text-[#5C5C59]">…e mais {filteredContacts.length - 200} não exibidos</div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm">Mensagem principal</Label>
              <p className="text-[10px] text-[#5C5C59]">Use {'{nome}'} ou {'{primeiro_nome}'} pra personalizar.</p>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="mass-message-input" placeholder="Olá {nome}! Tudo bem? ..." className="rounded-none mt-1 min-h-[100px]" />
            </div>

            <div className="p-3 bg-[#EDF3F0] border-l-[3px] border-[#0F4C3A]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-[#0F4C3A]">
                  📝 Variações da mensagem (recomendado)
                  <span className="ml-2 inline-block text-[10px] font-mono bg-white text-[#0F4C3A] px-1.5 py-0.5 border border-[#0F4C3A]/30" data-testid="mass-variations-counter">
                    {form.variations.length}/{MAX_VARIATIONS} liberadas
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={addVariation}
                  disabled={form.variations.length >= MAX_VARIATIONS}
                  data-testid="mass-add-variation-btn"
                  className="bg-white text-[#0F4C3A] hover:bg-[#F7F7F5] border border-[#0F4C3A] rounded-none disabled:opacity-40"
                >
                  <Plus size={12} weight="bold" className="mr-1" /> Adicionar
                </Button>
              </div>
              <p className="text-[10px] text-[#5C5C59] mb-2">
                Adicione até {MAX_VARIATIONS} versões diferentes. O sistema escolhe uma aleatória pra cada contato — reduz o risco de ban.
              </p>
              {form.variations.length === 0 && (
                <p className="text-[10px] italic text-[#5C5C59]">Nenhuma variação. Sem variações, todos recebem a mensagem principal (mais arriscado).</p>
              )}
              {form.variations.map((v, i) => (
                <div key={i} className="mt-2 grid grid-cols-[auto_1fr_auto] gap-2 items-start">
                  <div className="text-[10px] font-bold text-[#0F4C3A] pt-2">#{i + 2}</div>
                  <Textarea value={v} onChange={(e) => updateVariation(i, e.target.value)} data-testid={`mass-variation-${i}`} placeholder={`Versão ${i + 2} (use {nome}, {primeiro_nome})`} className="rounded-none min-h-[60px] text-xs" />
                  <button onClick={() => removeVariation(i)} className="text-[#DC2626] hover:opacity-70 px-1 pt-2"><Trash size={14} weight="bold" /></button>
                </div>
              ))}
            </div>

            <div>
              <Label className="text-sm">Idade do chip (define o intervalo entre envios)</Label>
              <Select value={form.chip_age} onValueChange={(v) => setForm({ ...form, chip_age: v })}>
                <SelectTrigger data-testid="mass-chip-age-select" className="rounded-none mt-1 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  {CHIP_AGE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      <div>
                        <div className="font-semibold">{o.label}</div>
                        <div className="text-[10px] text-[#5C5C59]">{o.detail}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#5C5C59] mt-1">
                💡 Chip Novo = intervalo maior (mais seguro pra evitar ban). Chip Antigo = intervalo menor (você já usa o número há meses).
              </p>
            </div>

            <div>
              <Label className="text-sm">Tipo de envio</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, send_type: "text", attachment_data: "", attachment_type: "", attachment_filename: "" })}
                  data-testid="mass-type-text-btn"
                  className={`px-4 py-3 text-sm font-semibold border transition ${form.send_type === "text" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white text-[#1A1A1A] border-[#E5E5E0] hover:border-[#0F4C3A]/50"}`}
                >
                  📄 Texto
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, send_type: "audio", attachment_data: "", attachment_type: "audio", attachment_filename: "" })}
                  data-testid="mass-type-audio-btn"
                  className={`px-4 py-3 text-sm font-semibold border transition ${form.send_type === "audio" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white text-[#1A1A1A] border-[#E5E5E0] hover:border-[#0F4C3A]/50"}`}
                >
                  🎤 Áudio
                </button>
              </div>
            </div>

            {form.send_type === "audio" ? (
              <div>
                <Label className="text-sm">Áudio (.mp3, .ogg, .wav, .m4a)</Label>
                {form.attachment_filename ? (
                  <div className="mt-1 flex items-center justify-between border border-[#E5E5E0] p-3 bg-[#F7F7F5]">
                    <div className="text-xs truncate">
                      🎤 <b>{form.attachment_filename}</b>
                    </div>
                    <button onClick={() => setAudioFile(null)} className="text-[#DC2626] hover:opacity-70"><X size={14} weight="bold" /></button>
                  </div>
                ) : (
                  <label className="mt-1 border-2 border-dashed border-[#0F4C3A]/40 p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-[#EDF3F0]/40 transition text-center">
                    <div className="text-2xl">🎤</div>
                    <div className="mt-1 text-xs text-[#5C5C59]">Clique para anexar áudio (máx 64MB)</div>
                    <input type="file" accept=".mp3,.ogg,.wav,.m4a,audio/*" data-testid="mass-audio-file" className="hidden" onChange={(e) => setAudioFile(e.target.files?.[0])} />
                  </label>
                )}
              </div>
            ) : (
              <div>
                <Label className="text-sm">Anexo (Imagem, Vídeo ou PDF) — opcional</Label>
                {form.attachment_filename ? (
                  <div className="mt-1 flex items-center justify-between border border-[#E5E5E0] p-3 bg-[#F7F7F5]">
                    <div className="text-xs truncate">
                      📎 <b>{form.attachment_filename}</b> <span className="text-[10px] text-[#5C5C59]">({form.attachment_type})</span>
                    </div>
                    <button onClick={() => setMediaFile(null)} className="text-[#DC2626] hover:opacity-70"><X size={14} weight="bold" /></button>
                  </div>
                ) : (
                  <label className="mt-1 border-2 border-dashed border-[#0F4C3A]/40 p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-[#EDF3F0]/40 transition text-center">
                    <div className="text-2xl">📎</div>
                    <div className="mt-1 text-xs text-[#5C5C59]">Clique para anexar arquivo (máx 64MB)</div>
                    <input type="file" accept="image/*,video/*,.pdf,.doc,.docx" data-testid="mass-media-file" className="hidden" onChange={(e) => setMediaFile(e.target.files?.[0])} />
                  </label>
                )}
              </div>
            )}

            <div className="p-3 bg-[#F7F7F5] border border-[#E5E5E0]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-1.5">🕒 Agendar envio</div>
                  <div className="text-[10px] text-[#5C5C59]">Dispara automaticamente na data e hora escolhida.</div>
                </div>
                <Switch checked={scheduleOn} onCheckedChange={setScheduleOn} data-testid="mass-schedule-switch" className="data-[state=checked]:bg-[#0F4C3A]" />
              </div>
              {scheduleOn && (
                <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="mass-scheduled-at" className="rounded-none mt-2" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={saveCampaign} disabled={savingCamp} data-testid="mass-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
              <CheckCircle size={16} weight="bold" className="mr-1.5" />
              {savingCamp ? "Salvando…" : "Criar envio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, highlight, danger }) {
  return (
    <div className="bg-white border border-[#E5E5E0] shadow-brutal p-4">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">{label}</div>
      <div className={`text-3xl font-black tracking-tighter mt-1 ${highlight ? "text-[#0F4C3A]" : danger ? "text-[#DC2626]" : "text-[#1A1A1A]"}`}>{value}</div>
    </div>
  );
}

function emptyForm() {
  return {
    name: "",
    message: "",
    variations: [],
    contacts: [],
    chip_age: "moderado",
    send_type: "text",
    attachment_data: "",
    attachment_filename: "",
    attachment_mimetype: "",
    attachment_type: "",
    scheduled_at: "",
  };
}

function mergeContacts(existing, incoming) {
  const map = new Map(existing.map((c) => [c.phone, c]));
  for (const c of incoming) map.set(c.phone, c);
  return Array.from(map.values());
}
